import { defineStore } from 'pinia';
import API from '@/shared/helpers/API.js';
import {useOrdersStore, useUserStore, useUnchangingStore, useRoomsOrdersStore, useNativeStore} from "@/console-new/stores";
import {useSunmiStore} from "@/console-sun/stores/sunmi.store";
import {useNotificationStore} from "@/shared/stores";

export const useAblyStore = defineStore('ably', {
	state: () => ({
		ably: null,
		isSuspended: false,
		activeChannels: [],
		pendingPrintReceipts: 0
	}),
	getters: {
	},
	actions: {
		startAbly(callback = null) {

			if(this.ably === null) {

				this.ably = new Ably.Realtime({
					authCallback: (tokenParams, callback) => {

						API.init().getAblyAuthToken((response) => {
							if(response && response.result === "OK") {
								callback(null, response.token_request);
							} else {
								callback(new Error('Failed to get Ably auth token:' + response.details ? response.details : 'Unknown error'));
							}
						});
					}
				});
				this.ably.connection.once('connected', () => {
					callback && callback();
				});
				this.ably.connection.on('connected', () => {
					const unchangingStore = useUnchangingStore();
					unchangingStore.offline = false;
					this.isSuspended = false;
					console.log("Connection to ably established");
				});
				this.ably.connection.on('disconnected', () => {
					const unchangingStore = useUnchangingStore();
					unchangingStore.offline = true;
					console.log("Connection to ably disconnected");
				});
				this.ably.connection.on('suspended', () => {
					//TODO flag to check fiscal orders when connection is restored
					console.log("Connection to ably suspended");
					this.isSuspended = true;
				});
			} else {
				callback && callback();
			}

		},
		subscribeToChannel(channelKey, listener, callback = null) {
			if(this.activeChannels.includes(channelKey)) {
				return;
			}
			this.activeChannels.push(channelKey);

			const channel = this.ably.channels.get(channelKey);
			channel.subscribe(listener).then(() => {
				console.log('Subscribed to channel: ' + channelKey);
				callback && callback();
			});

		},
		unsubscribeFromChannel(channelKey) {
			const channel = this.ably.channels.get(channelKey);
			if(channel) {
				channel.unsubscribe();
				try {
					channel.detach();
					console.log("Unsubscribed from channel: " + channelKey);
				} catch (e) {
					console.error("Error unsubscribing from channel: " + channelKey, e);
				}
				this.activeChannels = this.activeChannels.filter(c => c !== channelKey);
			}
		},

		subscribeToOpsUpdates(callback = null) {
			const userStore = useUserStore();
			if(userStore.currBusinessId === null) {
				console.log('No business selected, cannot subscribe to updates');
				return;
			}
			const channelKey = ENVIRONMENT+':business-ops:'+userStore.currBusinessId;
			this.subscribeToChannel(channelKey, this.opsUpdatesListener, callback);
		},
		unsubscribeFromOpsUpdates() {
			const userStore = useUserStore();
			if(userStore.currBusinessId === null) {
				console.log('No business selected, cannot unsubscribe from updates');
				return;
			}
			const channelKey = ENVIRONMENT+':business-ops:'+userStore.currBusinessId;
			this.unsubscribeFromChannel(channelKey);
		},
		opsUpdatesListener(message) {
			//console.log('Received a ABLY operation message ('+message.name+'): '+JSON.stringify(message.data));

			let excludedStations = useNativeStore().excludedStations.map(s => s !== "null" ? parseInt(s) : s);

			if(message.name === 'new-order') {

				const ordersStore = useOrdersStore();
				ordersStore.getNewOrderData(message.data.order_id, (order) => {
					if(order) {
						// Check if order already has a fiscal receipt for local Sunmi printing
						const sunmiStore = useSunmiStore();
						const userStore = useUserStore();
						if(order.receipt_id && sunmiStore.canLocalPrint && sunmiStore.expectingFiscalPrint
							&& userStore?.currBusiness?.apiscontrino_id && order.payment_user_id
							&& Number(order.payment_user_id) === Number(userStore.user?.user_id)) {
							this.fetchAndLocalPrintReceipt([order.receipt_id]);
						}

						if(!excludedStations.includes(order.payment_user_id ? Number(order.payment_user_id) : "null")) {
							if(order.paid || order.type === "multi") {
								ordersStore.getFiscalUpdates();
							}
						}
					}
				});

			} else if(message.name === 'order-update') {
				if(!message?.data || !Array.isArray(message.data?.orders)) {
					return;
				}
				const ordersStore = useOrdersStore();
				ordersStore.applyOrdersUpdates(message.data.orders, true, message.data.payments);

				const sunmiStore = useSunmiStore();
				const userStore = useUserStore();

				let receiptIdsToPrint = [];
				let printedPaymentIds = [];
				let hadPrintWork = false;

				let receiptsToEnqueue = [];

				const canPrint = sunmiStore.canLocalPrint && sunmiStore.expectingFiscalPrint && userStore?.currBusiness?.apiscontrino_id;

				message.data.orders.forEach((order) => {

					const isCurrentUser = order.payment_user_id && Number(order.payment_user_id) === Number(userStore?.user?.user_id);

					if(canPrint && isCurrentUser) {
						if(order.receipt_id && !order.fiscal_error) {
							if(!receiptIdsToPrint.includes(Number(order.receipt_id))) {
								receiptIdsToPrint.push(Number(order.receipt_id));
							}
						} else if(!order.payments?.length && order.fiscal_error === 'ade_error' && order.ade_receipt_data) {
							// Non-multi orders: ADE errors handled here (no payment-level entries exist)
							receiptsToEnqueue.push({
								business: {
									name: userStore.currBusiness?.name || "",
									address: userStore.currBusiness?.address?.via || "",
									civic: userStore.currBusiness?.address?.civico || null,
									zip: userStore.currBusiness?.address?.zip || "",
									city: userStore.currBusiness?.address?.comune || "",
									province: userStore.currBusiness?.address?.provincia_codice || "",
									vat: userStore.currBusiness?.vat || "",
								},
								receipt: order.ade_receipt_data.receipt,
								additionalLines: order.ade_receipt_data.additionalLines || [],
								adeError: true
							});
							hadPrintWork = true;
						}
					}

					// Skip excluded stations
				if(!order?.hasOwnProperty("payment_user_id") || !excludedStations.includes(order.payment_user_id ? Number(order.payment_user_id) : "null")) {
						// Epson fiscal updates
						if(
							(order.hasOwnProperty("new_order_payment") && order.new_order_payment)
							|| (order.hasOwnProperty("paid") && order.paid)
						) {
							ordersStore.getFiscalUpdates();
						}
					}
				});

				// Collect from payments (handles multi-payment orders + dedup by payment_id)
				if(canPrint && message.data.payments?.length > 0) {
					message.data.payments.forEach((payment) => {
						const isCurrentUser = payment.payment_user_id && Number(payment.payment_user_id) === Number(userStore?.user?.user_id);
						if(!isCurrentUser) return;

						// Skip if we already processed this payment_id
						if(payment.payment_id && printedPaymentIds.includes(Number(payment.payment_id))) return;
						if(payment.payment_id) printedPaymentIds.push(Number(payment.payment_id));

						if(payment.receipt_id && !payment.fiscal_error) {
							if(!receiptIdsToPrint.includes(Number(payment.receipt_id))) {
								receiptIdsToPrint.push(Number(payment.receipt_id));
							}
						} else if(payment.fiscal_error === 'ade_error' && payment.ade_receipt_data) {
							receiptsToEnqueue.push({
								business: {
									name: userStore.currBusiness?.name || "",
									address: userStore.currBusiness?.address?.via || "",
									civic: userStore.currBusiness?.address?.civico || null,
									zip: userStore.currBusiness?.address?.zip || "",
									city: userStore.currBusiness?.address?.comune || "",
									province: userStore.currBusiness?.address?.provincia_codice || "",
									vat: userStore.currBusiness?.vat || "",
								},
								receipt: payment.ade_receipt_data.receipt,
								additionalLines: payment.ade_receipt_data.additionalLines || [],
								adeError: true
							});
							hadPrintWork = true;
						}
					});
				}

				if(hadPrintWork) {
					useNotificationStore().showNotification('fiscal-receipt-ade-error');
				}

				if(receiptsToEnqueue.length > 0) {
					sunmiStore.enqueueReceipt(receiptsToEnqueue);
				}

				if(receiptIdsToPrint.length > 0 || hadPrintWork) {
					this.fetchAndLocalPrintReceipt(receiptIdsToPrint);
				}

			} else if(message.name === 'call-update') {

				const roomsOrdersStore = useRoomsOrdersStore();
				//TODO this is currently replacing the existing calls, effectively handling only the latest call
				// we should request the full list of calls on init (similar to fiscal prints)
				roomsOrdersStore.initRoomsTablesCalls([message.data]);

			}
		},
		fetchAndLocalPrintReceipt(receiptIds) {
			const sunmiStore = useSunmiStore();
			if(!sunmiStore.expectingFiscalPrint) return;

			const userStore = useUserStore();
			if(!userStore.currBusinessId || !receiptIds?.length) {
				if(this.pendingPrintReceipts <= 0) {
					sunmiStore.expectingFiscalPrint = false;
				}
				return;
			}

			const batchSize = receiptIds.length;
			let pending = batchSize;
			this.pendingPrintReceipts += batchSize;
			let printingData = [];
			let afterFetchFunction = (printingArray) => {
				if(printingArray?.length) {
					sunmiStore.enqueueReceipt(printingArray);
				} else {
					useNotificationStore().showNotification('receipt-print-error');
				}
				this.pendingPrintReceipts -= batchSize;
				if(this.pendingPrintReceipts <= 0) {
					this.pendingPrintReceipts = 0;
					sunmiStore.expectingFiscalPrint = false;
				}
			}
			receiptIds.forEach((receiptId) => {
				API.init().getFiscalReceiptData(userStore.currBusinessId, receiptId, (response) => {
					if(response?.result === "OK" && response?.data) {
						printingData.push(response.data);
					}
					pending--;
					if(pending <= 0) {
						afterFetchFunction(printingData);
					}
				});
			});
		},
	}
});
